/**
 * Flight-local time.
 *
 * A flight's times are shown in the time zone it was flown in, not the zone of
 * the browser reading it. A UK viewer opening a Californian log should see the
 * time the pilot saw, 13:48 PDT, not 21:48 BST.
 *
 * The zone comes from the server with the parsed flight (meta.timeZone:
 * { name, abbreviation }), looked up once from the take-off point at the
 * flight's own time. Here, Intl turns the IANA name into the wall-clock time and
 * the UTC offset for the flight's date, so daylight saving is always right.
 *
 * Anything without a zone shows UTC, labelled as such. The browser's own zone is
 * never used: a time that silently depends on who is looking is exactly the bug
 * this replaces.
 *
 * Display only. Stored and transmitted times stay in UTC epoch seconds.
 */

const formatters = new Map();

function formatter(key, options) {
  const id = `${key}|${options.timeZone}`;
  let f = formatters.get(id);
  if (!f) {
    f = new Intl.DateTimeFormat(key === 'offset' ? 'en-US' : undefined, options);
    formatters.set(id, f);
  }
  return f;
}

/**
 * The IANA zone name to format in, or 'UTC' when there is none or this browser
 * does not recognise it.
 *
 * @param {{name?:string}|null|undefined} tz meta.timeZone
 */
export function flightZone(tz) {
  const name = tz && typeof tz.name === 'string' ? tz.name : '';
  if (!name) return 'UTC';

  try {
    formatter('check', { timeZone: name });
    return name;
  } catch {
    return 'UTC';
  }
}

/** Minutes east of UTC for this zone at this instant. */
function offsetMinutes(date, zone) {
  const parts = {};
  for (const p of formatter('offset', {
    timeZone: zone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)) {
    parts[p.type] = Number(p.value);
  }

  const wall = Date.UTC(parts.year, parts.month - 1, parts.day,
    parts.hour === 24 ? 0 : parts.hour, parts.minute, parts.second);
  const instant = Math.floor(date.getTime() / 1000) * 1000;

  return Math.round((wall - instant) / 60000);
}

function offsetText(minutes) {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
}

/**
 * "PDT (UTC-7)", "BST (UTC+1)", "UTC+4" where the zone has no alphabetic
 * abbreviation, or "UTC" when there is no zone.
 */
export function zoneLabel(epochSeconds, tz) {
  const zone = flightZone(tz);
  if (zone === 'UTC') return 'UTC';

  const offset = offsetText(offsetMinutes(new Date(epochSeconds * 1000), zone));
  const abbr = tz && typeof tz.abbreviation === 'string' && /^[A-Z]{2,5}$/.test(tz.abbreviation)
    ? tz.abbreviation
    : null;

  return abbr ? `${abbr} (${offset})` : offset;
}

/**
 * Wall-clock time in the flight's zone, labelled.
 * "13:48:37 PDT (UTC-7)", or with the date "Sep 24, 2026, 13:48:37 PDT (UTC-7)".
 * Date and time follow the reader's locale; only the zone is the flight's.
 */
export function flightClock(epochSeconds, tz, withDate = false) {
  if (!epochSeconds) return '—';

  const zone = flightZone(tz);
  const time = formatter('time', {
    timeZone: zone, hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).format(new Date(epochSeconds * 1000));
  const stamp = `${time} ${zoneLabel(epochSeconds, tz)}`;

  return withDate ? `${flightDate(epochSeconds, tz)}, ${stamp}` : stamp;
}

/** The calendar date in the flight's zone, e.g. "Sep 24, 2026". */
export function flightDate(epochSeconds, tz) {
  if (!epochSeconds) return '—';

  return formatter('date', {
    timeZone: flightZone(tz), day: 'numeric', month: 'short', year: 'numeric',
  }).format(new Date(epochSeconds * 1000));
}
